import { PAYMENT_METHODS, isCardPaymentMethod } from '../constants/payment-methods.js';
import { NON_CREDIT_INSTALLMENTS_REFUSAL, installmentsRefusal } from './payment-fee.js';
export const RECEIPT_ERROR_CODES = [
    'RECEIPT_V2_DISABLED',
    'RECEIPT_EMPTY',
    'RECEIPT_INVALID_AMOUNT',
    'RECEIPT_DUPLICATE_PART',
    'RECEIPT_DUPLICATE_TRANSACTION',
    'RECEIPT_PAYMENT_METHOD_REQUIRED',
    'RECEIPT_CREDIT_FIELDS_INVALID',
    'RECEIPT_CARD_FIELDS_INVALID',
    'RECEIPT_PART_EQUATION_INVALID',
    'RECEIPT_ALLOCATION_SUM_MISMATCH',
    'RECEIPT_ALLOCATION_TARGET_UNKNOWN',
    'RECEIPT_TRANSACTION_NOT_ALLOCATED',
    'RECEIPT_EXCESS_RESOLUTION_REQUIRED',
    'RECEIPT_EXCESS_RESOLUTION_NOT_ALLOWED',
    'RECEIPT_EFFECTIVE_DATE_INVALID',
    'RECEIPT_EFFECTIVE_DATE_REASON_REQUIRED',
    'RECEIPT_CLOSED_PERIOD',
    'RECEIPT_OVER_ALLOCATION',
    'RECEIPT_OUTSTANDING_REASON_REQUIRED',
    'RECEIPT_REVISION_CONFLICT',
    'RECEIPT_TRANSACTION_NOT_FOUND',
    'RECEIPT_TRANSACTION_NOT_RECEIVABLE',
    'RECEIPT_EXPENSE_NOT_SUPPORTED',
    'RECEIPT_PATIENT_REQUIRED',
    'RECEIPT_PATIENT_MISMATCH',
    'RECEIPT_CREDIT_INSUFFICIENT',
    'IDEMPOTENCY_KEY_REUSED',
    'REFUND_TRACKING_NOT_FOUND',
    'REFUND_TRACKING_NOT_PENDING',
    'REFUND_TRACKING_REVISION_CONFLICT',
    'REFUND_CASH_REGISTER_REQUIRED',
    // Story 65.20: perfil, bandeira e taxa da parte.
    'RECEIPT_PROFILE_INVALID',
    'RECEIPT_CARD_BRAND_REQUIRED',
    'RECEIPT_FEE_OVERFLOW',
    'RECEIPT_PART_NOT_FOUND',
    'RECEIPT_PART_REVISION_CONFLICT',
    'FEE_REVISION_INVALID',
    'FEE_REVISION_REASON_REQUIRED',
    'FEE_ZERO_REQUIRES_CONFIRMATION',
    'FEE_REVISION_NOT_APPLICABLE',
    // 65.41: pagamento em dinheiro não tem taxa (no caixa, o custo do dinheiro é zero de fato).
    'FEE_REVISION_CASH_HAS_NO_FEE',
    'FEE_REVISION_NO_CHANGE',
    // RC-117: pagamento corrigido por inteiro não tem valor aplicado para receber taxa.
    'FEE_REVISION_NO_BASE',
    // 65.27: a taxa desta parte vem de eventos de custo; o ajuste é feito no evento.
    'FEE_REVISION_BY_EVENT',
    // Story 65.21: distribuição da taxa e correção de pagamento.
    'FEE_DISTRIBUTION_INVALID',
    'FEE_DISTRIBUTION_REASON_REQUIRED',
    'FEE_DISTRIBUTION_PENDING',
    'FEE_DISTRIBUTION_TOTAL_MISMATCH',
    'CORRECTION_INVALID',
    'CORRECTION_REASON_REQUIRED',
    'CORRECTION_TARGET_NOT_FOUND',
    'CORRECTION_TARGET_MISMATCH',
    'CORRECTION_AMOUNT_EXCEEDS',
    'CORRECTION_RESOLUTION_NOT_ALLOWED',
    'CORRECTION_PATIENT_REQUIRED',
    'CORRECTION_PREVIEW_STALE',
    'CORRECTION_ORIGIN_UNAVAILABLE',
];
/** Maior valor aceito por campo (int4 do banco). */
export const RECEIPT_MAX_CENTS = 2_147_483_647;
const CIVIL_DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const isCents = (value) => typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= RECEIPT_MAX_CENTS;
/**
 * Folha de uma parte nova: dinheiro devolve a diferença como troco; os demais meios guardam a
 * diferença como excedente (que precisa de destino). Crédito não tem valor entregue.
 */
export function receiptLeafOf(part) {
    if (part.sourceType === 'patient_credit') {
        return {
            sourceType: 'patient_credit',
            paymentMethod: null,
            receivedAmount: null,
            appliedAmount: part.appliedAmount,
            changeAmount: 0,
            excessAmount: 0,
            correctionReturnAmount: 0,
        };
    }
    const received = part.receivedAmount ?? part.appliedAmount;
    const difference = received - part.appliedAmount;
    const cash = part.paymentMethod === 'cash';
    return {
        sourceType: 'payment_method',
        paymentMethod: part.paymentMethod ?? null,
        receivedAmount: received,
        appliedAmount: part.appliedAmount,
        changeAmount: cash ? difference : 0,
        excessAmount: cash ? 0 : difference,
        correctionReturnAmount: 0,
    };
}
/**
 * Equações de uma folha (AC12). `corrected` é a folha que substitui outra numa correção: só ela
 * pode ter valor devolvido por correção. Devolve `null` quando a folha fecha.
 */
export function checkReceiptLeafEquation(leaf, corrected = false) {
    const amounts = [leaf.appliedAmount, leaf.changeAmount, leaf.excessAmount, leaf.correctionReturnAmount];
    if (!amounts.every(isCents))
        return 'valores precisam ser inteiros em centavos, sem negativo';
    if (leaf.sourceType === 'patient_credit') {
        if (leaf.receivedAmount !== null)
            return 'crédito da cliente não tem valor entregue';
        if (leaf.paymentMethod !== null)
            return 'crédito da cliente não tem forma de pagamento';
        if (leaf.changeAmount || leaf.excessAmount || leaf.correctionReturnAmount) {
            return 'crédito da cliente não tem troco, excedente nem devolução';
        }
        return null;
    }
    if (leaf.paymentMethod === null)
        return 'meio externo precisa da forma de pagamento';
    if (!isCents(leaf.receivedAmount))
        return 'meio externo precisa do valor entregue, inteiro em centavos';
    if (!corrected && leaf.correctionReturnAmount !== 0)
        return 'folha sem correção não tem valor devolvido por correção';
    if (leaf.paymentMethod === 'cash' && leaf.excessAmount !== 0)
        return 'dinheiro não gera excedente: a diferença é troco';
    if (leaf.paymentMethod !== 'cash' && leaf.changeAmount !== 0)
        return 'só dinheiro tem troco';
    const total = leaf.appliedAmount + leaf.changeAmount + leaf.excessAmount + leaf.correctionReturnAmount;
    if (leaf.receivedAmount !== total)
        return 'o valor entregue precisa ser a soma de aplicado, troco, excedente e devolvido';
    return null;
}
/** Regras do pedido que não dependem do banco. Lista vazia = pedido bem formado. */
export function validateReceiptRequest(request) {
    const violations = [];
    const add = (code, message, extra = {}) => {
        violations.push({ code, message, ...extra });
    };
    const expected = request.expectedTransactions ?? [];
    const parts = request.parts ?? [];
    if (!expected.length)
        add('RECEIPT_EMPTY', 'Informe as cobranças deste recebimento.');
    if (!parts.length)
        add('RECEIPT_EMPTY', 'Informe ao menos uma forma de pagamento.');
    const expectedIds = new Set();
    for (const item of expected) {
        if (expectedIds.has(item.transactionId)) {
            add('RECEIPT_DUPLICATE_TRANSACTION', 'A mesma cobrança aparece duas vezes no pedido.', { transactionId: item.transactionId });
        }
        expectedIds.add(item.transactionId);
        if (!Number.isInteger(item.expectedRevision) || item.expectedRevision < 1) {
            add('RECEIPT_REVISION_CONFLICT', 'Versão da cobrança inválida.', { transactionId: item.transactionId });
        }
    }
    const partIds = new Set();
    const allocated = new Set();
    for (const part of parts) {
        const at = { clientPartId: part.clientPartId };
        if (!part.clientPartId || partIds.has(part.clientPartId)) {
            add('RECEIPT_DUPLICATE_PART', 'Cada parte precisa de uma identificação própria.', at);
        }
        partIds.add(part.clientPartId);
        if (!isCents(part.appliedAmount) || part.appliedAmount === 0) {
            add('RECEIPT_INVALID_AMOUNT', 'O valor aplicado precisa ser inteiro em centavos e maior que zero.', at);
            continue;
        }
        if (part.effectiveDate !== undefined && !CIVIL_DATE.test(part.effectiveDate)) {
            add('RECEIPT_EFFECTIVE_DATE_INVALID', 'Data do pagamento inválida.', at);
        }
        if (part.effectiveDateReason !== undefined && (part.effectiveDate === undefined || part.effectiveDateReason.trim().length < 3)) {
            add('RECEIPT_EFFECTIVE_DATE_REASON_REQUIRED', 'O motivo da data diferente precisa de ao menos 3 letras e de uma data.', at);
        }
        if (part.sourceType === 'patient_credit') {
            if (part.paymentMethod !== undefined || part.receivedAmount !== undefined || part.installments !== undefined
                || part.cardBrand !== undefined || part.paymentMethodProfileId !== undefined) {
                add('RECEIPT_CREDIT_FIELDS_INVALID', 'Crédito da cliente não tem forma, valor entregue, cartão nem parcelas.', at);
            }
            if (part.excessResolution) {
                add('RECEIPT_EXCESS_RESOLUTION_NOT_ALLOWED', 'Crédito da cliente não gera excedente.', at);
            }
        }
        else if (part.sourceType === 'payment_method') {
            if (!part.paymentMethod || !PAYMENT_METHODS.includes(part.paymentMethod)) {
                add('RECEIPT_PAYMENT_METHOD_REQUIRED', 'Informe a forma de pagamento da parte.', at);
                continue;
            }
            if (part.receivedAmount !== undefined && (!isCents(part.receivedAmount) || part.receivedAmount < part.appliedAmount)) {
                add('RECEIPT_INVALID_AMOUNT', 'O valor entregue precisa ser inteiro em centavos e cobrir o aplicado.', at);
                continue;
            }
            if (part.installments !== undefined) {
                // 65.57: os textos do critério 6, os mesmos dos outros caminhos.
                const refusal = part.paymentMethod !== 'credit_card'
                    ? NON_CREDIT_INSTALLMENTS_REFUSAL
                    : installmentsRefusal(part.paymentMethod, part.installments);
                if (refusal)
                    add('RECEIPT_CARD_FIELDS_INVALID', refusal, at);
            }
            if (part.cardBrand !== undefined && !isCardPaymentMethod(part.paymentMethod)) {
                add('RECEIPT_CARD_FIELDS_INVALID', 'Bandeira vale só para cartão.', at);
            }
            const leaf = receiptLeafOf(part);
            const broken = checkReceiptLeafEquation(leaf);
            if (broken)
                add('RECEIPT_PART_EQUATION_INVALID', `Parte inconsistente: ${broken}.`, at);
            if (leaf.excessAmount > 0) {
                if (!part.excessResolution || part.excessResolution.amount !== leaf.excessAmount) {
                    add('RECEIPT_EXCESS_RESOLUTION_REQUIRED', 'Diga o que fazer com o valor que passou: crédito da cliente ou devolução.', at);
                }
                else if (part.excessResolution.kind !== 'patient_credit' && part.excessResolution.kind !== 'refund_tracking') {
                    add('RECEIPT_EXCESS_RESOLUTION_REQUIRED', 'Destino do valor que passou inválido.', at);
                }
            }
            else if (part.excessResolution) {
                add('RECEIPT_EXCESS_RESOLUTION_NOT_ALLOWED', 'Esta parte não tem valor passando do aplicado.', at);
            }
        }
        else {
            add('RECEIPT_PAYMENT_METHOD_REQUIRED', 'Origem da parte inválida.', at);
            continue;
        }
        const allocations = part.allocations ?? [];
        const seen = new Set();
        let sum = 0;
        for (const allocation of allocations) {
            if (!isCents(allocation.amount) || allocation.amount === 0) {
                add('RECEIPT_INVALID_AMOUNT', 'Cada distribuição precisa ser inteira em centavos e maior que zero.', { ...at, transactionId: allocation.transactionId });
                continue;
            }
            if (seen.has(allocation.transactionId)) {
                add('RECEIPT_DUPLICATE_TRANSACTION', 'A mesma cobrança aparece duas vezes na mesma parte.', { ...at, transactionId: allocation.transactionId });
            }
            seen.add(allocation.transactionId);
            if (!expectedIds.has(allocation.transactionId)) {
                add('RECEIPT_ALLOCATION_TARGET_UNKNOWN', 'A parte distribui valor para uma cobrança fora do pedido.', { ...at, transactionId: allocation.transactionId });
            }
            allocated.add(allocation.transactionId);
            sum += allocation.amount;
        }
        if (sum !== part.appliedAmount) {
            add('RECEIPT_ALLOCATION_SUM_MISMATCH', 'A distribuição entre as cobranças precisa somar o valor aplicado da parte.', at);
        }
    }
    for (const id of expectedIds) {
        if (parts.length && !allocated.has(id)) {
            add('RECEIPT_TRANSACTION_NOT_ALLOCATED', 'Há cobrança no pedido sem nenhum valor distribuído.', { transactionId: id });
        }
    }
    if (request.partialReason !== undefined && request.partialReason.trim().length < 3) {
        add('RECEIPT_OUTSTANDING_REASON_REQUIRED', 'O motivo do recebimento parcial precisa de ao menos 3 letras.');
    }
    return violations;
}
/**
 * Saldo de cada cobrança depois deste recebimento. Nenhuma passa do próprio valor; saldo em
 * aberto exige o motivo do recebimento parcial.
 */
export function planReceiptObligations(obligations, parts, partialReason) {
    const now = new Map();
    for (const part of parts) {
        for (const allocation of part.allocations) {
            now.set(allocation.transactionId, (now.get(allocation.transactionId) ?? 0) + allocation.amount);
        }
    }
    const violations = [];
    const plan = obligations.map((obligation) => {
        const allocatedNow = now.get(obligation.transactionId) ?? 0;
        const balanceAfter = obligation.amount - obligation.alreadyAllocated - allocatedNow;
        if (balanceAfter < 0) {
            violations.push({
                code: 'RECEIPT_OVER_ALLOCATION',
                message: 'O valor distribuído passa do saldo da cobrança.',
                transactionId: obligation.transactionId,
            });
        }
        return { transactionId: obligation.transactionId, allocatedNow, balanceAfter, settled: balanceAfter === 0 };
    });
    if (!violations.length && plan.some((item) => item.balanceAfter > 0) && !(partialReason && partialReason.trim().length >= 3)) {
        violations.push({
            code: 'RECEIPT_OUTSTANDING_REASON_REQUIRED',
            message: 'Fica saldo em aberto: informe o motivo do recebimento parcial.',
        });
    }
    return { plan, violations };
}
/**
 * Rateio proporcional pelo maior resto: cada linha recebe o piso da sua parte e os centavos que
 * sobram vão para as maiores frações, com o id como desempate. A soma é sempre o total.
 */
export function splitByLargestRemainder(total, lines) {
    if (!Number.isInteger(total) || total < 0)
        throw new Error('total do rateio precisa ser inteiro em centavos');
    const weightSum = lines.reduce((sum, line) => sum + line.weight, 0);
    if (!lines.length || weightSum <= 0 || lines.some((line) => !Number.isInteger(line.weight) || line.weight < 0)) {
        throw new Error('pesos do rateio inválidos');
    }
    const shares = lines.map((line) => {
        const exact = total * line.weight;
        return { id: line.id, amount: Math.floor(exact / weightSum), remainder: exact % weightSum };
    });
    let left = total - shares.reduce((sum, share) => sum + share.amount, 0);
    const order = [...shares].sort((a, b) => b.remainder - a.remainder || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    for (const share of order) {
        if (left <= 0)
            break;
        share.amount += 1;
        left -= 1;
    }
    return shares.map(({ id, amount }) => ({ id, amount }));
}
//# sourceMappingURL=receipt.js.map